import type { SendParams } from '@/interfaces/searchParam'
import type { Query } from '@/types/general'
import type {
  CarModelResponse,
  CarModelContents,
} from '#shared/interfaces/car-model-list/model'
import type { StringMinMax } from '@/interfaces/global'

export default defineNuxtPlugin(() => {
  const nuxtApp = useNuxtApp()
  let controller: AbortController
  return {
    provide: {
      createSearchParams(): SendParams {
        const sendParams: SendParams = {
          newCarPrice: {
            min: -1,
            max: -1,
          },
          brands: [],
          bodyTypes: [],
          isCurrentModel: true,
          isForeignCar: false,
          isJapaneseCar: false,
          weight: [],
          height: [],
          carLength: [],
          width: [],
          ridingCapacity: {
            min: 0,
            max: 0,
          },
          driveTypes: [],
          mission: [],
          fuelCds: [],
          isSpecial: false,
          transition_source: '',
        }
        return sendParams
      },
      makeSearchConditions(sendParam: SendParams) {
        const query: Query = {}

        // ブランドコード
        if (sendParam.brands && sendParam.brands.length > 0) {
          const brandCds: string[] = []
          sendParam.brands.forEach((key) => {
            brandCds.push(key)
          })
          query.brand_cd = brandCds.join(',')
        }

        // ボディタイプコード
        if (sendParam.bodyTypes && sendParam.bodyTypes.length > 0) {
          const bodyTypeCds: string[] = []
          sendParam.bodyTypes.forEach((key) => {
            bodyTypeCds.push(key)
          })
          query.body_sub_cd = bodyTypeCds.join(',')
        }

        // 駆動方式
        if (sendParam.driveTypes && sendParam.driveTypes.length > 0) {
          query.drive_type = sendParam.driveTypes.join(',')
        }

        // ミッション
        if (sendParam.mission && sendParam.mission.length > 0) {
          query.mission = sendParam.mission.join(',')
        }

        // 燃料コード
        if (sendParam.fuelCds && sendParam.fuelCds.length > 0) {
          query.fuel_cd = sendParam.fuelCds.join(',')
        }

        // 現行販売車
        if (sendParam.isCurrentModel) {
          query.current_model_flg = 'true'
        }

        // 輸入車
        if (sendParam.isForeignCar) {
          query.foreign_car_flg = 'true'
        }

        // 国産車
        if (sendParam.isJapaneseCar) {
          query.japanese_car_flg = 'true'
        }

        // 限定特別使用車
        if (sendParam.isSpecial) {
          query.special_flg = 'true'
        }
        if (sendParam.newCarPrice.min > 0) {
          query.price_min = sendParam.newCarPrice.min.toString()
        }
        if (sendParam.newCarPrice.max > 0) {
          query.price_max = sendParam.newCarPrice.max.toString()
        }

        if (sendParam.carLength.length > 0) {
          sendParam.carLength.forEach((car: StringMinMax, i) => {
            if (car.min) {
              query[`length[${i}][min]`] = `${car.min}`
            }
            if (car.max) {
              query[`length[${i}][max]`] = `${car.max}`
            }
          })
        }

        if (sendParam.width.length > 0) {
          sendParam.width.forEach((car: StringMinMax, i) => {
            if (car.min) {
              query[`width[${i}][min]`] = `${car.min}`
            }
            if (car.max) {
              query[`width[${i}][max]`] = `${car.max}`
            }
          })
        }

        if (sendParam.height.length > 0) {
          sendParam.height.forEach((car: StringMinMax, i) => {
            if (car.min) {
              query[`height[${i}][min]`] = `${car.min}`
            }
            if (car.max) {
              query[`height[${i}][max]`] = `${car.max}`
            }
          })
        }

        if (sendParam.weight.length > 0) {
          sendParam.weight.forEach((car: StringMinMax, i) => {
            if (car.min) {
              query[`weight[${i}][min]`] = `${car.min}`
            }
            if (car.max) {
              query[`weight[${i}][max]`] = `${car.max}`
            }
          })
        }
        if (sendParam.ridingCapacity.min > 0) {
          query.riding_capacity_min = sendParam.ridingCapacity.min.toString()
        }
        if (sendParam.ridingCapacity.max > 0) {
          query.riding_capacity_max = sendParam.ridingCapacity.max.toString()
        }

        // transition_source
        if (sendParam.transition_source) {
          query.transition_source = sendParam.transition_source
        }

        return query
      },
      async getModelList(sendParam: SendParams) {
        if (controller) {
          controller.abort()
        }
        controller = new AbortController()
        let carModelContents: CarModelContents = {
          total_results_available: 0,
          cars: [],
        }
        await $fetch<CarModelResponse>('/api/car-model-list/models', {
          cache: 'no-cache',
          query: nuxtApp.$makeSearchConditions(sendParam),
          signal: controller.signal,
        }).then((result) => {
          if (result) {
            carModelContents = result.data
          }
        })
        return carModelContents
      },
      makeGradeSpecificationQuery(
        query: Query,
        carModelCd: string,
        currentModelFlg?: boolean,
        recentDebutYear?: number,
        recentDebutMonth?: number,
        conditionFindFlg?: boolean
      ) {
        // 車種モデルコード
        query.car_model_cd = carModelCd
        query.by_grade_flg = 'false'
        if (!conditionFindFlg) {
          if (currentModelFlg) {
            query.current_model_flg = 'true'
          } else {
            query.debut_year = recentDebutYear?.toString()
            query.debut_month = recentDebutMonth?.toString()
          }
        }
        return query
      },
      hasGradeSpecificationQuery(query: Query) {
        // ボディタイプ
        if (query.body_sub_cd) {
          return true
        }

        // 駆動
        if (query.drive_type) {
          return true
        }

        // ミッション
        if (query.mission) {
          return true
        }

        // 燃料
        if (query.fuel_cd) {
          return true
        }

        // 現行販売車
        if (query.current_model_flg) {
          return true
        }

        // 限定特別使用車
        if (query.special_flg) {
          return true
        }

        // 国産車
        if (query.japanese_car_flg) {
          return true
        }

        // 輸入車
        if (query.foreign_car_flg) {
          return true
        }

        // 新車価格
        if (query.price_min) {
          return true
        }
        if (query.price_max) {
          return true
        }

        // 車両全長
        if (query[`length[0][min]`] || query[`length[0][max]`]) {
          return true
        }

        // 車幅
        if (query[`width[0][min]`] || query[`width[0][max]`]) {
          return true
        }

        // 車高
        if (query[`height[0][min]`] || query[`height[0][max]`]) {
          return true
        }

        // 車両重量
        if (query[`weight[0][min]`] || query[`weight[0][max]`]) {
          return true
        }

        // 乗車定員
        if (query.riding_capacity_min) {
          return true
        }
        if (query.riding_capacity_max) {
          return true
        }
        return false
      },
    },
  }
})
